import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@restworld/utils-common';

const switchRootVariants = cva(
  'relative inline-flex items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2',
  {
    variants: {
      size: {
        sm: 'h-5 w-10 min-h-[20px] min-w-[40px]',
        md: 'h-7 w-14 min-h-[28px] min-w-[56px]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const switchThumbVariants = cva('inline-block rounded-full bg-white transition-transform', {
  variants: {
    size: {
      sm: 'h-4 w-4 min-w-[16px] min-h-[16px]',
      md: 'h-5 w-5 min-w-[20px] min-h-[20px]',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export type SwitchProps = VariantProps<typeof switchRootVariants> & {
  checked: boolean;
  onChange: (checked: boolean) => void;
  srOnlyLabel?: string;
  disabled?: boolean;
  className?: string;
};

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ size = 'md', checked, onChange, srOnlyLabel, disabled, className }, ref) => {
    return (
      <SwitchPrimitive.Root
        ref={ref}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className={cn(
          switchRootVariants({ size }),
          checked ? 'bg-purple-500' : 'bg-neutral-90',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          className
        )}
      >
        {srOnlyLabel && <span className="sr-only">{srOnlyLabel}</span>}
        <SwitchPrimitive.Thumb
          className={cn(
            switchThumbVariants({ size }),
            size === 'sm'
              ? checked
                ? 'translate-x-[21px]'
                : 'translate-x-[3px]'
              : checked
              ? 'translate-x-[31px]'
              : 'translate-x-[5px]'
          )}
        />
      </SwitchPrimitive.Root>
    );
  }
);
Switch.displayName = 'Switch';

export { switchRootVariants, switchThumbVariants };
